import { useEffect, useMemo, useState } from 'react';
import { BarChart3, ClipboardCheck, RefreshCw, Target, Users } from 'lucide-react';
import { loadDashboardData, type DashboardData } from '../../services/dashboard';
import './dashboard.css';

type Props = {
  schoolId: string;
  onNewAssessment: () => void;
};

const emptyData: DashboardData = {
  metrics: { assessments: 0, launches: 0, students: 0, skills: 0 },
  performance: { average: 0, high: 0, medium: 0, low: 0, evaluated: 0 },
  recentAssessments: [],
};

const subjectLabel: Record<string, string> = { PORTUGUESE: 'Língua Portuguesa', MATHEMATICS: 'Matemática' };
const statusLabel: Record<string, string> = { DRAFT: 'Rascunho', OPEN: 'Aberto', CLOSED: 'Encerrado' };

function dateLabel(value: string | null) {
  if (!value) return 'Sem data';
  const [y,m,d] = value.split('-');
  return `${d}/${m}/${y}`;
}

export function DashboardPage({ schoolId, onNewAssessment }: Props) {
  const [data, setData] = useState<DashboardData>(emptyData);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  async function refresh() {
    if (!schoolId) return;
    setLoading(true);
    setError('');
    try {
      setData(await loadDashboardData(schoolId));
    } catch (err) {
      console.error(err);
      setError('Não foi possível carregar os indicadores desta escola.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void refresh(); }, [schoolId]);

  const metrics = useMemo(() => [
    ['Simulados', data.metrics.assessments, 'Cadastrados na escola', ClipboardCheck, 'blue'],
    ['Aplicações', data.metrics.launches, 'Lançamentos registrados', BarChart3, 'green'],
    ['Estudantes', data.metrics.students, 'Ativos na escola', Users, 'amber'],
    ['Habilidades', data.metrics.skills, 'Disponíveis na matriz', Target, 'purple'],
  ], [data]);

  const bars = [
    ['Média geral', data.performance.average],
    ['80% ou mais', data.performance.high],
    ['60% a 79%', data.performance.medium],
    ['Abaixo de 60%', data.performance.low],
  ];

  return <div className="dash">
    <div className="page-title"><div><span className="eyebrow2">VISÃO GERAL</span><h1>Painel de Controle</h1><p>Dados reais da escola selecionada no Radar SAEB.</p></div><button onClick={onNewAssessment}>+ Novo simulado</button></div>

    {error && <div className="dash-error"><span>{error}</span><button onClick={()=>void refresh()}><RefreshCw size={16}/>Tentar novamente</button></div>}

    <section className="metric-grid" aria-busy={loading}>{metrics.map(([t,v,s,I,c]:any)=><article className="metric" key={t}><div className={'metric-icon '+c}><I size={23}/></div><div><small>{t}</small><strong>{loading?'—':v}</strong><span>{s}</span></div></article>)}</section>

    <section className="dash-grid">
      <article className="panel"><div className="panel-head"><div><h3>Desempenho geral</h3><p>{loading?'Carregando resultados...':`${data.performance.evaluated} aplicações avaliadas`}</p></div><span>2026</span></div><div className="bars">{bars.map(([n,v]:any)=><div className="bar-row" key={n}><label>{n}<b>{loading?'—':`${v}%`}</b></label><div><span style={{width:(loading?0:v)+'%'}}/></div></div>)}</div></article>

      <article className="panel"><div className="panel-head"><div><h3>Simulados recentes</h3><p>Últimos registros da escola</p></div></div>
        <div className="recent-list">
          {loading && <div className="recent-empty">Carregando...</div>}
          {!loading && !data.recentAssessments.length && <div className="recent-empty">Nenhum simulado cadastrado para esta escola.</div>}
          {!loading && data.recentAssessments.map(a=><button key={a.id} className="recent-row" onClick={()=>{}}><div><b>{a.name}</b><span>{a.grade}º ano · {subjectLabel[a.subject] || a.subject}</span></div><div><em className={`status status-${a.status.toLowerCase()}`}>{statusLabel[a.status] || a.status}</em><small>{dateLabel(a.assessment_date)}</small></div></button>)}
        </div>
      </article>
    </section>
  </div>;
}
